import { BookkeepingSuggestionApiClient, ListSuggestionsFilters, PaginationParams } from "@/clients/bookkeepingSuggestionApiClient";
import {
  BookkeepingSuggestion,
  BookkeepingSuggestionStatus,
  VerificationType,
  CreateSuggestionResponse,
  ListSuggestionsResponse,
  UpdateSuggestionStatusResponse,
  DeclineAndRetryResponse,
} from "@/types/bookkeepingSuggestion";

export class BookkeepingSuggestionService {
  private apiClient: BookkeepingSuggestionApiClient;

  constructor() {
    this.apiClient = new BookkeepingSuggestionApiClient();
  }

  /**
   * Create a bookkeeping suggestion for a payable or receivable
   */
  async createSuggestion(
    verificationType: VerificationType,
    verificationId: string
  ): Promise<CreateSuggestionResponse> {
    try {
      console.log(`BookkeepingSuggestionService: Creating suggestion for ${verificationType}: ${verificationId}`);
      return await this.apiClient.createSuggestion(verificationType, verificationId);
    } catch (error) {
      console.error("Error creating bookkeeping suggestion:", error);
      throw error;
    }
  }

  /**
   * List all pending bookkeeping suggestions
   */
  async listPendingSuggestions(
    filters?: ListSuggestionsFilters,
    pagination?: PaginationParams
  ): Promise<ListSuggestionsResponse> {
    try {
      console.log("BookkeepingSuggestionService: Listing pending suggestions");
      return await this.apiClient.listPendingSuggestions(filters, pagination);
    } catch (error) {
      console.error("Error listing pending suggestions:", error);
      throw error;
    }
  }

  /**
   * Get a specific bookkeeping suggestion by ID
   */
  async getSuggestion(id: string): Promise<BookkeepingSuggestion | null> {
    try {
      console.log(`BookkeepingSuggestionService: Getting suggestion ${id}`);
      return await this.apiClient.getSuggestion(id);
    } catch (error) {
      console.error("Error getting suggestion:", error);
      throw error;
    }
  }

  /**
   * Approve a bookkeeping suggestion
   */
  async approveSuggestion(
    id: string,
    selectedSuggestionId: string,
    approvalReason?: string
  ): Promise<UpdateSuggestionStatusResponse> {
    try {
      console.log(`BookkeepingSuggestionService: Approving suggestion ${id} with alternative ${selectedSuggestionId}`);
      return await this.apiClient.updateSuggestionStatus(id, 'approved', selectedSuggestionId, undefined, approvalReason);
    } catch (error) {
      console.error("Error approving suggestion:", error);
      throw error;
    }
  }

  /**
   * Decline a suggestion and queue a retry with user context
   */
  async declineAndRetry(
    id: string,
    declineContext: string
  ): Promise<DeclineAndRetryResponse> {
    try {
      console.log(`BookkeepingSuggestionService: Declining suggestion ${id} and requesting retry`);
      return await this.apiClient.declineAndRetry(id, declineContext);
    } catch (error) {
      console.error("Error declining and retrying suggestion:", error);
      throw error;
    }
  }

  /**
   * Mark suggestion for manual review (when max retries reached)
   */
  async markForManualReview(
    id: string,
    reason: string
  ): Promise<BookkeepingSuggestion> {
    try {
      console.log(`BookkeepingSuggestionService: Marking suggestion ${id} for manual review`);
      return await this.apiClient.markForManualReview(id, reason);
    } catch (error) {
      console.error("Error marking suggestion for manual review:", error);
      throw error;
    }
  }
}
